import { useState } from 'react'
import { Search, ArrowUpDown, ChevronLeft, ChevronRight } from 'lucide-react'
import { cn, formatNumber, formatPercent } from '@/shared/lib/utils'
import { useKeywords } from '../hooks/useGFO'

const sortOptions = [
  { value: 'opportunity_score', label: 'Score d\'opportunité' },
  { value: 'impressions', label: 'Impressions' },
  { value: 'clicks', label: 'Clics' },
  { value: 'position', label: 'Position' },
]

function ScoreBadge({ score }: { score: number }) {
  const color =
    score >= 7 ? 'bg-red-50 text-red-600 border-red-200' :
    score >= 4 ? 'bg-amber-50 text-amber-600 border-amber-200' :
    'bg-emerald-50 text-emerald-600 border-emerald-200'

  return (
    <span className={cn('inline-flex items-center gap-1 rounded-lg border px-2 py-0.5 text-xs font-semibold', color)}>
      {score.toFixed(1)}
    </span>
  )
}

export function KeywordsPage() {
  const [page, setPage] = useState(1)
  const [sortBy, setSortBy] = useState('opportunity_score')
  const [search, setSearch] = useState('')

  const { data, isLoading } = useKeywords(page, sortBy)
  const keywords = data?.keywords || []
  const meta = data?.meta

  const filtered = search
    ? keywords.filter((k: any) => k.keyword.toLowerCase().includes(search.toLowerCase()))
    : keywords

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-2xl font-bold text-ink tracking-tight">Mots-clés</h1>
        <p className="text-sm text-ink-faint mt-1">
          Analyse Google Search Console — triée par potentiel d'optimisation
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative max-w-sm flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un mot-clé..."
            className="w-full rounded-card border border-ink-border bg-white pl-10 pr-4 py-2.5 text-sm placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-gold/20 focus:border-gold transition-all"
          />
        </div>
        <div className="flex items-center gap-2">
          <ArrowUpDown size={14} className="text-ink-faint" />
          <select
            value={sortBy}
            onChange={(e) => { setSortBy(e.target.value); setPage(1) }}
            className="rounded-card border border-ink-border bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/20 focus:border-gold"
          >
            {sortOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-card border border-ink-border bg-white shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink-border">
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-ink-faint">Mot-clé</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-ink-faint">Impressions</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-ink-faint">Clics</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-ink-faint">CTR</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-ink-faint">Position</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-ink-faint">Opportunité</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-border/30">
              {isLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 6 }).map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-4 w-20 animate-pulse rounded bg-paper-warm" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-ink-faint">
                    Aucun mot-clé trouvé
                  </td>
                </tr>
              ) : (
                filtered.map((kw: any) => (
                  <tr key={kw.id} className="hover:bg-paper/50 transition-colors">
                    <td className="px-4 py-3">
                      <span className="font-medium text-ink">{kw.keyword}</span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-ink">
                      {formatNumber(kw.impressions)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-ink">
                      {formatNumber(kw.clicks)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-ink">
                      {formatPercent(kw.ctr)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-ink">
                      {kw.position?.toFixed(1)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ScoreBadge score={kw.opportunity_score || 0} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {meta && (
          <div className="flex items-center justify-between border-t border-ink-border px-4 py-3">
            <p className="text-xs text-ink-faint">
              {meta.total} résultat{meta.total > 1 ? 's' : ''}
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="rounded-lg p-1.5 hover:bg-paper-warm disabled:opacity-30 transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="px-3 py-1 text-xs font-medium text-ink-muted">
                Page {page}
              </span>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={keywords.length < 20}
                className="rounded-lg p-1.5 hover:bg-paper-warm disabled:opacity-30 transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
